<script setup>
import { ref, onMounted, onUnmounted, computed, watch, defineAsyncComponent } from 'vue'
import { useUserStore } from '@/stores/user'
import { useAppStore } from '@/stores/app'
import { useGeoStore } from '@/stores/geo'
import { useLayersStore } from '@/stores/layers'
import { useBoundaryStore } from '@/stores/boundary'
import { useRoute, useRouter } from 'vue-router'
import {bbox} from '@turf/turf'

import { toast } from '@/components/ui/toast'
const FeaturesList = defineAsyncComponent(() => import('@/components/features/FeaturesList.vue'))
import { Spinner } from '@/components/ui/spinner'
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev,
} from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Filter, ChevronDown, ChevronUp, X } from 'lucide-vue-next'
import ProjectFilters from '@/pages/Layers/Detail/ProjectFilters.vue'
import ProjectTabs from '@/pages/Project/Components/ProjectTabs.vue'
import { useFeaturesFeed } from '@/composables/useFeaturesFeed'

const userStore = useUserStore()

const appStore = useAppStore()

const geoStore = useGeoStore()

const layersStore = useLayersStore()

const boundaryStore = useBoundaryStore()
const member = computed(() => userStore.member)
const isLoggedIn = computed(() => appStore.isLoggedIn)
const map = computed(() => geoStore.map)
const features = computed(() => geoStore.feedFeatures)

const dataFilter = ref('all') // 'all', 'private'
const filtersKey = ref(0)
const filtersMounted = ref(false)
const isFetchingBoundary = ref(false)

const router = useRouter()
const route = useRoute()
const projectId = computed(() => parseInt(route.params.project_id))
const orgId = computed(() => route.params.org_id)

const additionalParams = computed(() => {
  const memberId = member.value?.member_id
  if (dataFilter.value === 'private' && memberId) {
    return { member_ids: String(memberId) }
  }
  return null
})

const {
  total,
  page,
  limit,
  offset,
  isLoading,
  viewportOnly,
  showFilters,
  currentFilters,
  hasActiveFilters,
  displayFeatures,
  fetchFeatures,
  fetchDates,
  toggleViewportOnly: composableToggleViewportOnly,
  updateFilters,
  paginationUpdated,
} = useFeaturesFeed({ orgId, projectId, layerCode: null, additionalParams })

const displayFeaturesLength = computed(() => displayFeatures.value?.length)
const featuresLength = computed(() => features.value.features?.length)
const isFeatureCreated = computed(() => geoStore.isFeatureCreated)

onMounted(async () => {
  if (isLoggedIn.value) {
    await fetchDates()
    await fetchFeatures()
  }
})

onUnmounted(() => {
  layersStore.clearDates()
  geoStore.setProjectFilters(null)
})

watch(
  () => isLoggedIn.value,
  async (newValue, oldValue) => {
    if (newValue && !oldValue) {
      await fetchDates()
      await fetchFeatures()
    }
  }
)

watch(
  () => projectId.value,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      layersStore.clearDates()
      await fetchDates()
      await fetchFeatures()
    }
  }
)

watch(
  () => isFeatureCreated.value,
  async (value) => {
    if (value) {
      await fetchFeatures()
      geoStore.setFeatureCreated(false)
    }
  }
)

watch(
  () => JSON.stringify(additionalParams.value),
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      page.value = 1
      await fetchFeatures()
    }
  }
)

const focusOnFilteredData = async () => {
  isFetchingBoundary.value = true
  try {
    const boundary = await boundaryStore.fetchProjectBoundary(projectId.value)

    if (!boundary) {
      isFetchingBoundary.value = false
      return
    }

    const geometry = boundary.geometry

    if (!geometry || !Array.isArray(geometry.coordinates) || geometry.coordinates.length === 0) {
      isFetchingBoundary.value = false
      return
    }

    const bounds = bbox(geometry)
    if (!bounds || bounds.length !== 4) {
      isFetchingBoundary.value = false
      return
    }
    map.value.fitBounds(bounds)
  } catch (error) {
    console.error('Failed to fetch project boundary', error)
  } finally {
    isFetchingBoundary.value = false
  }
}

const setDataFilter = async (filter) => {
  dataFilter.value = filter
}

watch(showFilters, (open) => {
  if (open) filtersMounted.value = true
})

const isFiltered = computed(() => hasActiveFilters.value || dataFilter.value === 'private')

const clearFilters = () => {
  dataFilter.value = 'all'
  viewportOnly.value = false
  filtersKey.value++
  updateFilters(null)
}

const toggleViewportOnly = async (value) => {
  await composableToggleViewportOnly(value)
  if (!value) {
    await focusOnFilteredData()
  }
}
</script>

<template>
  <ProjectTabs />

  <div class="px-3 py-2">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2 text-muted-foreground">
        <span>Total: {{ total }}</span>
      </div>
      <div class="flex items-center">
        <Button
          @click="showFilters = !showFilters"
          variant="ghost"
          size="sm"
          :class="isFiltered ? 'text-primary hover:bg-primary/10' : 'text-muted-foreground'"
        >
          <Filter class="h-4 w-4 mr-1" />
          Filter
          <component :is="showFilters ? ChevronUp : ChevronDown" class="h-4 w-4 ml-1" />
        </Button>
        <Button
          v-if="isFiltered"
          @click="clearFilters"
          variant="ghost"
          size="sm"
          class="text-primary hover:bg-primary/10"
          title="Clear all filters"
        >
          <X class="h-4 w-4 mr-1" />
          Clear
        </Button>
      </div>
    </div>

    <div v-show="showFilters" class="border rounded-lg p-3 mt-2 bg-muted/50 space-y-3">
      <div class="flex items-center">
        <Switch :model-value="viewportOnly" @update:model-value="toggleViewportOnly" />
        <span class="text-sm font-medium ml-2">Show data in map area</span>
      </div>

      <div class="flex items-center">
        <Switch
          :model-value="dataFilter === 'private'"
          @update:model-value="setDataFilter($event ? 'private' : 'all')"
        />
        <span class="text-sm font-medium ml-2">Show only my data</span>
      </div>

      <ProjectFilters
        v-if="filtersMounted"
        :key="filtersKey"
        :org-id="orgId"
        :project-id="projectId"
        :hide-project-selector="true"
        @update:filters="updateFilters"
      />
    </div>
  </div>

  <div v-if="displayFeaturesLength > 0">
    <!-- Features List with Selection -->
    <div class="relative">
      <div :class="{ 'opacity-50 transition-opacity duration-200': isLoading }">
        <FeaturesList :features="displayFeatures" />
      </div>
      <div v-if="isLoading" class="absolute inset-x-0 top-2 flex justify-center pointer-events-none">
        <span class="text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded shadow-sm">Updating...</span>
      </div>
    </div>

    <div v-if="total > limit && !isLoading" class="p-3">
      <Pagination
        v-slot="{ page }"
        :total="total"
        :sibling-count="1"
        show-edges
        :default-page="page"
        :items-per-page="limit"
        @update:page="paginationUpdated"
      >
        <PaginationList v-slot="{ items }" class="flex items-center gap-1">
          <PaginationFirst />
          <PaginationPrev />

          <template v-for="(item, index) in items">
            <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === page ? 'default' : 'outline'">
                {{ item.value }}
              </Button>
            </PaginationListItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>

          <PaginationNext />
          <PaginationLast />
        </PaginationList>
      </Pagination>
    </div>
  </div>
  <div v-else class="p-3 w-full h-full flex items-center justify-center">
    <div v-if="!isLoading && featuresLength === 0" class="mt-0 text-center">
      <div v-if="viewportOnly">
        <h3 class="text-base font-bold">No data in view</h3>
        <p class="text-muted-foreground">Try panning or zooming out to find data.</p>
        <Button
          @click="toggleViewportOnly(false)"
          class="text-primary mt-3"
          variant="secondary"
          size="sm"
        >
          Switch to all data
        </Button>
      </div>
      <div v-else-if="dataFilter === 'private'">
        <h3 class="text-base font-bold">You have no data here yet</h3>
        <p class="text-muted-foreground">Only data you created is shown.</p>
        <Button
          @click="setDataFilter('all')"
          class="text-primary mt-3"
          variant="secondary"
          size="sm"
        >
          Show all data
        </Button>
      </div>
      <div v-else>
        <h3 class="text-base font-bold">No data found</h3>
        <p class="text-muted-foreground">
          Adjust filters or select a different project.
        </p>
      </div>
    </div>
  </div>
</template>
